import React, { useState } from 'react';
import type { IconType } from 'react-icons';
import {
  FiActivity,
  FiAlertCircle,
  FiArrowRight,
  FiCheckCircle,
  FiChevronDown,
  FiCloud,
  FiServer,
  FiSettings,
  FiShield,
  FiSmartphone,
} from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { PROJECT_INQUIRY_URL } from '../constants/contact';

type ScopeItem = {
  number: string;
  title: string;
  description: string;
  icon: IconType;
};

const situations = [
  '기존 개발자와 연락이 되지 않는 경우',
  '앱이나 웹에서 오류가 반복되는 경우',
  'Android·iOS 정책 대응이 필요한 경우',
  '오래된 라이브러리와 개발 환경을 업데이트해야 하는 경우',
  '운영하면서 작은 기능을 계속 개선해야 하는 경우',
  '서버, 배포, 스토어 상태를 정기적으로 관리해야 하는 경우',
];

const maintenanceScopes: ScopeItem[] = [
  { number: '01', title: '오류 및 장애 대응', description: '앱·웹 오류 확인, 원인 분석, 긴급 수정 및 재배포', icon: FiAlertCircle },
  { number: '02', title: 'OS 및 정책 대응', description: 'Android·iOS 버전 대응, 스토어 정책 변경 및 심사 대응', icon: FiSmartphone },
  { number: '03', title: '기능 개선', description: '기존 기능 수정, 관리자 기능 보완, 소규모 기능 추가', icon: FiSettings },
  { number: '04', title: '서버 및 API 관리', description: '서버 오류 확인, API 수정, 배치 및 데이터 처리 점검', icon: FiServer },
  { number: '05', title: '배포 및 스토어 관리', description: '앱 빌드, 인증서 관리, 앱스토어·플레이스토어 업데이트', icon: FiCloud },
  { number: '06', title: '정기 점검', description: '서비스 상태, 오류 로그, 보안 및 주요 기능 정기 확인', icon: FiShield },
];

const processSteps = [
  '서비스 및 소스 구조 확인',
  '유지보수 가능 여부와 위험 요소 안내',
  '요청 접수 및 우선순위 협의',
  '개발 및 내부 테스트',
  '검수 후 운영 환경 반영',
  '작업 내역과 변경 사항 공유',
];

const operationNotes = [
  '지정된 채널을 통한 요청 접수',
  '요청별 진행 상태 공유',
  '배포 전 검수본 제공',
  '월별 작업 내역 정리',
  '별도 작업은 사전 협의 후 진행',
];

const maintenanceTypes = [
  { title: '건별 유지보수', description: '오류 수정이나 업데이트처럼 필요한 작업을 개별적으로 진행합니다.' },
  { title: '월 정기 유지보수', description: '매월 일정한 작업 시간을 확보해 오류 대응과 기능 개선을 지속적으로 진행합니다.', featured: true },
  { title: '운영 지원', description: '서버, 앱 배포, 스토어 심사, 관리자 운영 등 서비스 운영 전반을 지원합니다.' },
];

const maintenanceTasks = [
  '오래된 Android·iOS 프로젝트 최신 환경 업데이트',
  '앱스토어 및 플레이스토어 정책 변경 대응',
  '로그인과 푸시 알림 오류 수정',
  '결제, 회원, 관리자 기능 개선',
  '서버 이전 및 API 오류 대응',
  '기업 홈페이지 콘텐츠와 기능 개선',
  '타 개발사 프로젝트 인수 후 구조 정리',
];

const takeoverChecks = [
  '소스코드 보유 여부',
  '서버 및 스토어 계정 접근 가능 여부',
  '사용 중인 기술과 개발 환경',
  '현재 발생 중인 오류와 미완료 기능',
  '외부 서비스 및 API 사용 현황',
  '기존 데이터와 운영 중단 위험',
];

const faqItems = [
  { question: '다른 개발사가 만든 서비스도 유지보수가 가능한가요?', answer: '네, 가능합니다. 기존 소스코드와 서버, 배포 환경을 먼저 점검한 뒤 유지보수 가능 여부와 필요한 작업 범위를 안내드립니다. 다만 소스코드가 없거나 구조가 지나치게 불안정한 경우에는 일부 재개발이 필요할 수 있습니다.' },
  { question: '작은 수정도 요청할 수 있나요?', answer: '네, 문구 변경이나 이미지 교체, 화면 수정, 기능 보완 등 작은 작업도 요청하실 수 있습니다. 작업 내용을 확인한 뒤 단건으로 진행하거나, 요청이 자주 발생하는 경우 월 유지보수 방식으로 안내드립니다.' },
  { question: '긴급 오류도 대응 가능한가요?', answer: '서비스 접속 장애, 로그인 오류, 결제 문제, 앱 실행 불가 등 운영에 직접적인 영향을 주는 오류는 우선순위를 높여 확인합니다. 다만 정확한 대응 가능 시간은 오류 원인과 서버·소스 접근 가능 여부에 따라 달라질 수 있습니다.' },
  { question: '월 유지보수 비용은 어떻게 결정되나요?', answer: '서비스 규모, 운영 환경, 예상 요청 건수, 대응 시간, 서버 관리 여부 등을 기준으로 결정됩니다. 단순 수정 위주의 기본 관리부터 정기 업데이트와 장애 대응이 포함된 형태까지 필요한 범위에 맞춰 구성할 수 있습니다.' },
  { question: '앱스토어 심사도 진행해주시나요?', answer: '네, 가능합니다. 안드로이드와 iOS 앱 업데이트, 최신 운영체제 대응, 스토어 정책 변경에 따른 수정, 빌드 파일 생성, 심사 제출과 반려 대응까지 지원합니다. 계정 권한과 기존 프로젝트 상태를 확인한 뒤 필요한 작업 범위를 안내드립니다.' },
];

const cardTitleClass = 'font-semibold leading-[1.42] tracking-[-0.02em] text-slate-950';

const SectionHeading: React.FC<{ eyebrow: string; title: React.ReactNode; description?: React.ReactNode }> = ({ eyebrow, title, description }) => (
  <div className="max-w-[720px]">
    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#005322] md:text-sm">{eyebrow}</p>
    <h2 className="mt-4 break-keep text-2xl font-bold leading-[1.33] tracking-[-0.03em] text-slate-950 md:leading-[1.26] lg:text-[38px] lg:leading-[1.22]">
      {title}
    </h2>
    {description && <p className="mt-5 break-keep text-base leading-7 text-slate-600 md:text-[17px] md:leading-8">{description}</p>}
  </div>
);

const Maintenance: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(0);

  return (
    <main className="bg-white text-slate-950">
      <section className="border-b border-slate-200 bg-white py-12 md:py-16 lg:flex lg:items-center lg:pb-5 lg:pt-8">
        <div className="mx-auto w-full max-w-[1200px] px-4">
          <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,0.48fr)_minmax(0,0.52fr)] lg:gap-x-8">
          <div className="order-2 max-w-[720px] lg:order-none">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#005322] md:text-sm">MAINTENANCE</p>
            <div className="mt-4 h-px w-8 bg-[#005322]" />
            <h1 className="mt-8 break-keep text-3xl font-bold leading-[1.33] tracking-[-0.03em] text-slate-950 md:text-4xl md:leading-[1.26] lg:text-[36px] lg:leading-[1.22] xl:text-[38px]">
              개발이 끝난 뒤에도,<br />서비스는 계속 운영되어야 합니다.
            </h1>
            <p className="mt-6 max-w-[650px] break-keep text-[15px] leading-7 text-slate-600 md:text-base md:leading-7">
              운영 중 발생하는 오류부터 기능 개선, 서버 관리, 스토어 정책 대응까지<br className="hidden md:block" />
              기존 서비스의 구조를 파악하고 안정적으로 관리합니다.
            </p>
            <Link to={PROJECT_INQUIRY_URL} className="mt-8 inline-flex h-12 items-center justify-center gap-2 border border-[#005322] bg-[#005322] px-6 text-sm font-semibold text-white transition hover:bg-[#003a18]">
              유지보수 상담하기 <FiArrowRight className="text-base" />
            </Link>
            <p className="mt-5 max-w-[640px] break-keep border-l-2 border-[#005322] pl-4 text-sm leading-6 text-slate-600">
              타 개발사가 제작한 웹·앱도 소스코드와 운영 환경을 확인한 후 인수할 수 있습니다.
            </p>
          </div>

          <div className="order-1 flex min-h-[260px] items-center justify-center bg-white sm:min-h-[360px] lg:order-none lg:min-h-[420px]">
            <img
              src="/assets/images/maintenance/maintenance-hero.png"
              alt="유지보수 점검, 수정, 배포 및 운영 관리 과정을 보여주는 그래픽"
              className="h-auto w-full max-w-[720px] object-contain"
              decoding="async"
            />
          </div>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <SectionHeading eyebrow="WHEN SUPPORT IS NEEDED" title={<>운영은 계속되는데,<br />개발 지원이 멈춰 있나요?</>} />
          <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {situations.map((situation) => (
              <article key={situation} className="flex min-h-[112px] items-start gap-3 border border-slate-200 bg-white p-5">
                <FiCheckCircle className="mt-0.5 shrink-0 text-lg text-[#005322]" />
                <p className="break-keep text-[15px] font-medium leading-6 text-slate-700">{situation}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <SectionHeading eyebrow="MAINTENANCE SCOPE" title={<>서비스 운영에 필요한 영역을<br />함께 관리합니다.</>} />
          <div className="mt-10 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {maintenanceScopes.map((scope) => {
              const Icon = scope.icon;
              return (
                <article key={scope.number} className="group flex min-h-[224px] flex-col border border-slate-200 bg-white p-6 transition duration-200 hover:-translate-y-1 hover:border-[#005322]/30">
                  <div className="flex items-center justify-between">
                    <span className="flex h-10 w-10 items-center justify-center border border-[#005322]/15 bg-[#005322]/[0.06] text-[#005322]">
                      <Icon className="text-[20px]" />
                    </span>
                    <span className="text-xs font-semibold tracking-[0.14em] text-[#005322]">{scope.number}</span>
                  </div>
                  <h3 className={`mt-7 text-xl ${cardTitleClass}`}>{scope.title}</h3>
                  <p className="mt-3 break-keep text-sm leading-6 text-slate-600">{scope.description}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
            <SectionHeading eyebrow="HOW WE OPERATE" title="요청만 처리하고 끝내지 않습니다." description={<>작업 접수부터 테스트, 배포, 변경 내역 공유까지<br className="hidden md:block" />현재 진행 상황을 확인할 수 있도록 관리합니다.</>} />
          </div>
          <ol className="mt-10 grid gap-x-5 gap-y-8 border-y border-slate-200 py-8 md:grid-cols-2 xl:grid-cols-3">
            {processSteps.map((step, index) => (
              <li key={step} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center border border-[#005322]/20 bg-[#005322]/[0.06] text-xs font-bold text-[#005322]">{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <p className={`text-[15px] ${cardTitleClass}`}>{step}</p>
                  <p className="mt-1 text-sm leading-6 text-slate-500">진행 기준과 확인 내용을 정리해 공유합니다.</p>
                </div>
              </li>
            ))}
          </ol>
          <div className="mt-6 flex flex-wrap gap-x-5 gap-y-3">
            {operationNotes.map((note) => (
              <p key={note} className="inline-flex items-center gap-2 text-sm text-slate-600"><FiCheckCircle className="text-[#005322]" />{note}</p>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50 py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <SectionHeading eyebrow="MAINTENANCE PLANS" title={<>서비스 상황에 맞는 방식으로<br />진행할 수 있습니다.</>} />
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {maintenanceTypes.map((type) => (
              <article key={type.title} className={`flex min-h-[210px] flex-col border bg-white p-6 ${type.featured ? 'border-[#005322] border-t-4' : 'border-slate-200'}`}>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#005322]">{type.featured ? 'REGULAR SUPPORT' : 'MAINTENANCE'}</p>
                <h3 className={`mt-5 text-xl ${cardTitleClass}`}>{type.title}</h3>
                <p className="mt-3 break-keep text-sm leading-6 text-slate-600">{type.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <SectionHeading eyebrow="WORK EXAMPLES" title="이런 작업을 진행하고 있습니다." />
          <div className="mt-10 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {maintenanceTasks.map((task) => (
              <article key={task} className="flex min-h-[112px] items-start gap-3 border border-slate-200 bg-white p-5">
                <FiActivity className="mt-0.5 shrink-0 text-lg text-[#005322]" />
                <p className="break-keep text-[15px] font-medium leading-6 text-slate-700">{task}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-slate-50 py-16 md:py-20">
        <div className="mx-auto grid max-w-[1200px] gap-10 px-4 lg:grid-cols-[0.85fr_1.15fr] lg:gap-16">
          <SectionHeading eyebrow="BEFORE TAKEOVER" title={<>안정적인 운영을 위해<br />먼저 프로젝트 상태를 확인합니다.</>} />
          <div>
            <div className="grid gap-x-6 gap-y-4 border-y border-slate-200 py-6 md:grid-cols-2">
              {takeoverChecks.map((item) => (
                <p key={item} className="flex items-start gap-3 break-keep text-[15px] leading-6 text-slate-700">
                  <FiCheckCircle className="mt-0.5 shrink-0 text-lg text-[#005322]" />{item}
                </p>
              ))}
            </div>
            <p className="mt-6 border-l-2 border-[#005322] pl-4 break-keep text-sm leading-6 text-slate-600">
              소스코드 또는 주요 계정이 없거나 프로젝트 상태에 큰 문제가 있는 경우에는 유지보수보다 재구축을 안내드릴 수 있습니다.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 py-16 md:py-20">
        <div className="mx-auto grid max-w-[1200px] gap-10 px-4 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
          <SectionHeading eyebrow="FAQ" title="자주 묻는 질문" description="문의 전 확인이 필요한 내용을 정리했습니다." />
          <div className="border-t border-slate-200">
            {faqItems.map((item, index) => {
              const isOpen = openFaq === index;
              const panelId = `maintenance-faq-panel-${index}`;
              const buttonId = `maintenance-faq-button-${index}`;
              return (
                <div key={item.question} className="border-b border-slate-200">
                  <button
                    id={buttonId}
                    type="button"
                    className="flex w-full items-center justify-between gap-6 py-5 text-left"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                  >
                    <span className="break-keep text-[15px] font-semibold leading-6 text-slate-900 md:text-base">Q. {item.question}</span>
                    <FiChevronDown className={`shrink-0 text-xl text-[#005322] transition-transform duration-300 ease-out ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    aria-hidden={!isOpen}
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                  >
                    <div className="min-h-0 overflow-hidden">
                      <div className="pb-5 pr-8">
                        <p className="break-keep text-sm leading-7 text-slate-600 md:text-[15px]">A. {item.answer}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1200px] px-4">
          <div className="flex flex-col gap-8 border border-slate-900 bg-slate-900 p-7 text-white md:p-10 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-200">MAINTENANCE INQUIRY</p>
              <h2 className="mt-4 break-keep text-2xl font-bold leading-[1.33] tracking-[-0.03em] md:text-3xl md:leading-[1.26]">운영 중인 서비스를<br />안정적으로 이어가야 한다면</h2>
              <p className="mt-4 break-keep text-sm leading-6 text-slate-300 md:text-[15px]">현재 서비스 상태와 필요한 작업을 알려주세요.<br className="hidden md:block" />소스와 운영 환경을 확인한 뒤 적합한 유지보수 방식을 안내드립니다.</p>
            </div>
            <Link to={PROJECT_INQUIRY_URL} className="inline-flex h-12 shrink-0 items-center justify-center gap-2 border border-white bg-white px-6 text-sm font-semibold text-slate-950 transition hover:bg-slate-100">
              유지보수 상담하기 <FiArrowRight className="text-base" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Maintenance;
